import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Info, AlertOctagon, X, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SystemStatusBanner — Iter-160.
 *
 * Small, always-visible strip at the top of the app that admins toggle when
 * there's an active production incident. During the outage last evening
 * nobody was told anything; this banner is the fix. Polls `/api/system/status`
 * once on mount and every 60s while the tab is visible.
 *
 * Design notes:
 *  - Sits ABOVE the offline banner so both can co-exist during a real event.
 *  - `sessionStorage` remembers a "dismissed" message hash so the same
 *    banner doesn't re-appear on every route change once acknowledged.
 *  - Never re-renders when the response is `{active: false}` — zero visual
 *    footprint when everything's fine.
 */
const STORAGE_KEY = 'aayi_system_status_dismissed_v1';

const severityConfig = {
  info: {
    bg: 'bg-[#EEF2FF]',
    border: 'border-[#3730A3]/30',
    text: 'text-[#1E3A8A]',
    accent: 'text-[#3730A3]',
    Icon: Info,
    label: 'Notice',
  },
  warning: {
    bg: 'bg-[#FEF3C7]',
    border: 'border-[#F4B333]',
    text: 'text-[#78420F]',
    accent: 'text-[#B8860B]',
    Icon: AlertTriangle,
    label: 'Heads up',
  },
  critical: {
    bg: 'bg-[#FEE2E2]',
    border: 'border-[#B84B4B]',
    text: 'text-[#7F1D1D]',
    accent: 'text-[#B83826]',
    Icon: AlertOctagon,
    label: 'Service alert',
  },
};

const SystemStatusBanner = () => {
  const [status, setStatus] = useState(null);
  const [dismissedHash, setDismissedHash] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
  });

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/system/status`, { timeout: 6000 });
        if (!cancelled) setStatus(r.data || null);
      } catch {
        // If the status endpoint itself is unreachable we don't want to
        // spam the console — the OfflineBanner will handle that state.
      }
    };
    poll();
    const schedule = () => {
      if (document.hidden) return;
      timer = setTimeout(async () => { await poll(); schedule(); }, 60_000);
    };
    schedule();
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  if (!status?.active || !status?.message) return null;

  // Rehash the message text so a NEW incident re-shows even if the previous
  // one was dismissed. Session-scoped so a page reload keeps the ack.
  const hash = `${status.severity || 'info'}::${status.message}`;
  if (hash === dismissedHash) return null;

  const cfg = severityConfig[status.severity] || severityConfig.info;
  const Icon = cfg.Icon;

  const dismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, hash); } catch {}
    setDismissedHash(hash);
  };

  return (
    <div
      data-testid="system-status-banner"
      className={`sticky top-0 z-[60] w-full ${cfg.bg} border-b ${cfg.border} px-4 py-2`}
      role="status"
      aria-live="polite"
    >
      <div className="max-w-3xl mx-auto flex items-start gap-2">
        <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cfg.accent}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-black uppercase tracking-wider ${cfg.accent}`}>{cfg.label}</p>
          <p className={`text-xs leading-snug ${cfg.text}`}>
            {status.message}
            {status.link_url && (
              <a
                href={status.link_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="system-status-link"
                className={`ml-1 inline-flex items-center gap-0.5 underline ${cfg.accent} font-bold`}
              >
                Details <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss status message"
          data-testid="system-status-dismiss"
          className={`w-6 h-6 rounded-full ${cfg.text} hover:bg-black/5 flex items-center justify-center flex-shrink-0`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default SystemStatusBanner;
